import { useCallback, useMemo, useEffect } from 'react';
import { useIntl } from '@leagueplatform/locales';
import { useNavigateLegacy as useNavigate } from '@leagueplatform/routing';
import { useCommunicationPreferenceManager } from 'hooks/communications/use-communication-preference-manager.hook';
import { useCommunicationPreferenceMapper } from 'hooks/communications/use-communication-preference-mapper.hook';
import { useProductSegmentation } from 'hooks/use-product-segmentation.hook';
import {
  usePersonalInformation,
  useUpdateUserProfile,
} from 'hooks/use-personal-information.hook';
import { Address, CommunicationOption } from 'models/profile-settings.model';
import { sendSettingsScreenLoadedEvent } from 'utils/analytics-events';

interface UseProfileSettingsReturn {
  // Data
  data: any;
  preferences: any;
  initialOptions: CommunicationOption[];
  isLoading: boolean;
  fetchError: any;
  fetchErrorCode?: number | undefined;
  // Actions
  handlePersonalInfoSave: (values: Record<string, string>) => void;
  handleAddressInfoSave: (home: Address, mailing: Address) => void;
  handleCommunicationSave: (options: CommunicationOption[]) => void;
  handleGoBack: () => void;

  // UI helpers
  formatMessage: any;
  showPaperlessSetup: boolean;
}

export const useProfileSettings = (): UseProfileSettingsReturn => {
  const { formatMessage } = useIntl();
  const navigate = useNavigate();
  const { data } = usePersonalInformation();
  const { showPaperlessSetup } = useProductSegmentation();

  const { updateUserProfile: updatePersonalInfo } = useUpdateUserProfile({
    msg: 'STR_PROFILE_SETTING_SUCCESS_MSG',
  });
  const { updateUserProfile: updateAddress } = useUpdateUserProfile({
    msg: 'STR_PROFILE_SETTING_ADDRESS_SUCCESS_MSG',
  });

  const {
    preferences,
    isFetching,
    fetchError,
    fetchErrorCode,
    updatePreferences,
  } = useCommunicationPreferenceManager({
    showNotification: true,
  });
  const {
    mapApiToOptions,
    mapOptionsToApi,
    extractEmailFromPreferences,
    extractPhoneFromPreferences,
  } = useCommunicationPreferenceMapper();

  const emailFromPreferences = useMemo(
    () => extractEmailFromPreferences(preferences),
    [preferences, extractEmailFromPreferences],
  );

  const phoneFromPreferences = useMemo(
    () => extractPhoneFromPreferences(preferences),
    [preferences, extractPhoneFromPreferences],
  );

  const initialOptions = useMemo(
    () => mapApiToOptions(preferences, showPaperlessSetup),
    [preferences, showPaperlessSetup, mapApiToOptions],
  );

  // Use API data for initial options, fallback to defaults if API data is not available or failed
  const getDefaultOptions = useMemo((): CommunicationOption[] => {
    // If we have API data, use it
    if (preferences && Object.keys(preferences).length > 0) {
      return initialOptions;
    }

    // Use hardcoded defaults if no API data is available OR if API failed
    if (!showPaperlessSetup) {
      return [];
    }

    const defaultOptions: CommunicationOption[] = [
      {
        labelId: 'STR_PAPERLESS',
        value: true,
        type: 'radio',
        isBoolean: true,
      },
      {
        labelId: 'STR_PERSONALIZED_EMAIL',
        value: false,
        type: 'checkbox',
        isBoolean: true,
      },
      {
        labelId: 'STR_TIPS_AND_RECOMMENDATIONS',
        value: false,
        type: 'checkbox',
        isBoolean: true,
      },
      {
        labelId: 'STR_PRODUCT_UPDATES',
        value: false,
        type: 'checkbox',
        isBoolean: true,
      },
      {
        labelId: 'STR_TEXT',
        value: false,
        type: 'checkbox',
        isBoolean: true,
      },
      {
        labelId: 'STR_LANGUAGE',
        value: false,
        type: 'checkbox',
        isBoolean: true,
      },
    ];

    return defaultOptions;
  }, [showPaperlessSetup, preferences, initialOptions]);

  // Track screen loaded event
  useEffect(() => {
    sendSettingsScreenLoadedEvent('profile settings');
  }, []);

  const handlePersonalInfoSave = useCallback(
    (values: Record<string, string>) => {
      updatePersonalInfo({
        preferredFirstName: values.STR_PREFERRED_FIRST_NAME,
      });
    },
    [updatePersonalInfo],
  );

  const handleAddressInfoSave = useCallback(
    async (home: Address, mailing: Address) => {
      // Helper function to check if an address has meaningful values
      const hasAddressValues = (address: Address): boolean =>
        !!(
          address?.address1 ||
          address?.city ||
          address?.state ||
          address?.postal
        );

      // Build locations array, only including addresses with values
      const locations: Array<{
        type: 'mailing' | 'home';
        country: string;
        [key: string]: any;
      }> = [];

      if (hasAddressValues(mailing)) {
        locations.push({ type: 'mailing', country: 'US', ...mailing });
      }

      if (hasAddressValues(home)) {
        locations.push({ type: 'home', country: 'US', ...home });
      }

      // If no addresses have values, don't make the API call
      if (locations.length === 0) {
        return;
      }

      // Optimistic update - immediately update the UI
      // The queryClient.invalidateQueries in useUpdateUserProfile will refresh the data
      await updateAddress({ locations });
    },
    [updateAddress],
  );

  const handleCommunicationSave = useCallback(
    async (options: CommunicationOption[]) => {
      // Only try to update communication preferences if we have options
      if (options.length > 0) {
        // Use the true initial options from the API for comparison, fallback to defaults
        const trueInitialOptions = mapApiToOptions(
          preferences,
          showPaperlessSetup,
        );
        const payload = mapOptionsToApi(options, trueInitialOptions);
        await updatePreferences(payload);
      }
    },
    [
      preferences,
      showPaperlessSetup,
      mapApiToOptions,
      mapOptionsToApi,
      updatePreferences,
    ],
  );

  const handleGoBack = useCallback(() => {
    navigate(-1);
  }, [navigate]);

  // Only show loading when actively fetching, not when preferences are undefined
  const isLoading = isFetching;

  return {
    // Data
    data: {
      ...data,
      email: emailFromPreferences || '',
      phoneNumber: phoneFromPreferences || '',
    },
    preferences,
    initialOptions: getDefaultOptions,
    isLoading,
    fetchError,
    fetchErrorCode,
    // Actions
    handlePersonalInfoSave,
    handleAddressInfoSave,
    handleCommunicationSave,
    handleGoBack,

    // UI helpers
    formatMessage,
    showPaperlessSetup,
  };
};
